"use client";

import { HeartIcon, LocationPinIcon } from "icons";
import Link from "next/link";
import React, { useState } from "react";
import { Button as AriaButton } from "react-aria-components";
import {
  Heading,
  ImageWrapper,
  LinkButton,
  Text,
  TextButton,
} from "ui-components";

import { makeToast } from "@/src/components/Toast/Toast";
import { countries } from "@/src/constants/conutries";
import {
  JobResponseEntityType,
  SubBrandEntity,
  useJobFavouriteToggle,
} from "@/src/hooks/api/generated";
import { useAuth } from "@/src/hooks/useAuth";
import { concatString, decodeHTML, capitalize } from "@/src/utils/string";

import { DeleteJobModal } from "./DeleteJobModal";

export interface IJobCardProps {
  id: number;
  organizationId?: number;
  title: string;
  industry: string;
  city: string;
  country: string;
  type: JobResponseEntityType;
  coverImageUrl?: string;
  logoImageUrl?: string;
  organizationName?: string;
  organizationSlug?: string;
  isFavorite?: boolean;
  jobSlug?: string;
  source_internal_id?: string;
  onToggleFavorite?: (status: boolean) => void;
  isEditable?: boolean;
  refetch?: () => void;
  onClickEdit?: () => void;
  sub_brand_name?: string | null;
  subBrand?: SubBrandEntity | null;
}
export function JobsCard({
  id,
  organizationId,
  title,
  industry,
  city,
  country,
  type,
  coverImageUrl,
  logoImageUrl,
  organizationName,
  organizationSlug,
  isFavorite,
  jobSlug,
  source_internal_id,
  onToggleFavorite,
  isEditable,
  refetch,
  onClickEdit,
  sub_brand_name,
  subBrand,
}: IJobCardProps): JSX.Element {
  const { isAuthenticated } = useAuth();
  const [isOpenDeleteConfirmationModal, setIsOpenDeleteConfirmationModal] =
    useState(false);
  const { mutate: toggleFavourite } = useJobFavouriteToggle();
  const [isFavoriteJob, setIsFavoriteJob] = useState<boolean | undefined>(
    isFavorite
  );

  function handleFavouriteJobToggle(): void {
    if (!jobSlug) {
      return;
    }
    toggleFavourite(
      {
        data: {
          jobSlug,
        },
      },
      {
        onSuccess: () => {
          if (onToggleFavorite) {
            onToggleFavorite?.(!!isFavoriteJob);
          } else {
            setIsFavoriteJob(!isFavoriteJob);
          }
          makeToast({
            title: isFavoriteJob
              ? "You have removed a job."
              : "You have saved a job.",
            intent: isFavoriteJob ? "danger" : "success",
          });
        },
      }
    );
  }

  return (
    <div className="relative">
      <DeleteJobModal
        jobId={id}
        organizationId={organizationId}
        organizationSlug={organizationSlug}
        isOpen={isOpenDeleteConfirmationModal}
        onOpenChange={setIsOpenDeleteConfirmationModal}
        refetch={refetch}
      />
      {!!isAuthenticated && (
        <AriaButton
          className="absolute flex justify-center bg-white-200 bg-white items-center w-10 h-10 right-4 top-4 z-10 rounded-full outline-none"
          onPress={() => {
            handleFavouriteJobToggle();
          }}
        >
          <HeartIcon
            className={`w-6 h-6 text-brand-primary ${
              isFavoriteJob ? "fill-brand-primary" : ""
            }`}
          />
        </AriaButton>
      )}
      <div className="w-full min-h-[450px] rounded-[10px] border hover:shadow-lg overflow-hidden">
        <div className="relative w-full aspect-[259/100]">
          <ImageWrapper
            src={coverImageUrl}
            placeholderUrl="/organization-cover-fallback.jpg"
            alt={`${organizationName} cover image`}
            className="rounded-t-[10px] object-cover"
            fill
            isCLDImage
          />
        </div>

        <div className="p-5 flex flex-col gap-2 items-center">
          {(() => {
            const displayName =
              subBrand?.name || sub_brand_name || organizationName;
            const displayLogo = subBrand?.profile_picture || logoImageUrl;

            return (
              <div className="w-full flex items-center gap-4">
                <Link href={`/organizations/${organizationSlug}`}>
                  <ImageWrapper
                    src={displayLogo}
                    width={68}
                    height={68}
                    alt={`${displayName} logo`}
                    className="rounded-[10px] w-[68px] h-[68px] border"
                    style={{
                      objectFit: "contain",
                    }}
                    isCLDImage
                  />
                </Link>
                <div className="flex flex-col gap-1">
                  <Text variant="base-bold">{displayName}</Text>
                  <Text variant="small">{concatString(industry, 50)}</Text>
                </div>
              </div>
            );
          })()}
          <div className="w-full h-[80px]">
            <Heading variant="h4" className="line-clamp-2">
              {concatString(decodeHTML(title), 50)}
            </Heading>
          </div>
          <Text className="w-full line-clamp-2 h-10">
            <LocationPinIcon className="w-6 h-6 inline mr-2" />
            {capitalize(countries[country])}, {city}
          </Text>
          {type === JobResponseEntityType.remote && (
            <div className="flex justify-start w-full">
              <div className="border border-black border-1 rounded-3xl px-2.5 py-1 flex items-center">
                <Text variant="small">Remote</Text>
              </div>
            </div>
          )}
          <LinkButton className="my-4 h-12" href={`jobs/${jobSlug}`}>
            <Text>View Job</Text>
          </LinkButton>

          {isEditable && (
            <div className="flex flex-row gap-4">
              {!source_internal_id && (
                <TextButton
                  className="underline !text-blue-800"
                  onPress={() => onClickEdit?.()}
                >
                  Edit
                </TextButton>
              )}
              <TextButton
                className="underline text-danger"
                onPress={() => setIsOpenDeleteConfirmationModal(true)}
              >
                Delete
              </TextButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
